import { useEffect, useRef } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuth } from "./auth/AuthProvider";
import LoginPage from "./auth/LoginPage";
import SignupPage from "./auth/SignupPage";
import BroadcastPage from "./broadcast/BroadcastPage";
import ViewerPage from "./viewer/ViewerPage";
import ArchivePage from "./viewer/ArchivePage";
import EmergencyPage from "./emergency/EmergencyPage";
import { config } from "./config";

type SiteLanguage = "en" | "es" | "nl";

const SHELL_COPY: Record<SiteLanguage, {
  watch: string;
  emergency: string;
  crisis: string;
  archive: string;
  goLive: string;
  signOut: string;
  signIn: string;
  footer: string;
}> = {
  en: { watch: "Watch", emergency: "Emergency", crisis: "Crisis tools", archive: "Archive", goLive: "Go Live", signOut: "Sign out", signIn: "Sign in", footer: "live broadcast" },
  es: { watch: "Ver en directo", emergency: "Emergencia", crisis: "Herramientas de crisis", archive: "Archivo", goLive: "Transmitir", signOut: "Cerrar sesión", signIn: "Iniciar sesión", footer: "transmisión en directo" },
  nl: { watch: "Live kijken", emergency: "Noodmelding", crisis: "Crisishulpmiddelen", archive: "Archief", goLive: "Live uitzenden", signOut: "Uitloggen", signIn: "Inloggen", footer: "live-uitzending" },
};

function languageFromSearch(search: string): SiteLanguage {
  const value = new URLSearchParams(search).get("lang");
  return value === "es" || value === "nl" ? value : "en";
}

function localPath(path: string, language: SiteLanguage) {
  return `${path}?lang=${language}`;
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="center">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  const { user, signOut } = useAuth();
  const location = useLocation();
  const language = languageFromSearch(location.search);
  const copy = SHELL_COPY[language];
  const sentRef = useRef(new Set<string>());

  useEffect(() => {
    const path = `${location.pathname}${location.search}`;
    const key = `${path}|${document.title}`;
    if (sentRef.current.has(key)) return;
    sentRef.current.add(key);

    const payload = {
      path,
      title: document.title || "",
      referrer: document.referrer || "",
      sourceHost: window.location.host || "",
      visitorId: getOrCreateId("amigoe_live_visitor_id"),
      sessionId: getOrCreateId("amigoe_live_session_id", true),
    };
    const body = JSON.stringify(payload);

    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(config.trackerUrl, new Blob([body], { type: "text/plain" }));
        return;
      }
    } catch {
      // Fall back to fetch below.
    }

    void fetch(config.trackerUrl, {
      method: "POST",
      mode: "no-cors",
      headers: { "Content-Type": "text/plain" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  }, [location.pathname, location.search]);

  return (
    <div className="app">
      <header className="topbar">
        <Link to={localPath("/", language)} className="brand">
          <span className="dot" /> AmigoeAruba <em>Live</em>
        </Link>
        <nav>
          <Link to={localPath("/", language)}>{copy.watch}</Link>
          <Link to={localPath("/emergency", language)} className="emergency-link">{copy.emergency}</Link>
          <a href={localPath("/crisis", language)} className="crisis-link">{copy.crisis}</a>
          {user ? (
            <>
              <Link to={localPath("/archive", language)}>{copy.archive}</Link>
              <Link to="/broadcast">{copy.goLive}</Link>
              <button className="link" onClick={signOut}>{copy.signOut}</button>
            </>
          ) : (
            <Link to={localPath("/login", language)}>{copy.signIn}</Link>
          )}
        </nav>
      </header>

      <main>
        <Routes>
          <Route path="/" element={<ViewerPage />} />
          <Route path="/archive" element={<ArchivePage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/emergency" element={<EmergencyPage />} />
          <Route
            path="/broadcast"
            element={
              <RequireAuth>
                <BroadcastPage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <footer className="footer">
        <span>© ArubaTouristChannel.com · {copy.footer}</span>
      </footer>
    </div>
  );
}

function getOrCreateId(key: string, session = false): string {
  try {
    const store = session ? window.sessionStorage : window.localStorage;
    const existing = store.getItem(key);
    if (existing) return existing;
    const id = window.crypto?.randomUUID?.() ?? `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    store.setItem(key, id);
    return id;
  } catch {
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
  }
}
